import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../../../lib/api";
import type { CurrentUser } from "../../../lib/auth";
import { DialogPanel } from "../../../components/shared/DialogPanel";
import { XIcon } from "../../../components/shared/Icons";
import { backdropClose } from "../../../components/shared/backdrop";

const _authedRoute = getRouteApi("/_authed");

interface User {
  id: string;
  login: string;
  name: string;
  role_id: string;
  role_name: string;
  paused: boolean;
  paused_substitute_id: string | null;
  agents_count: number;
}

interface Role { id: string; name: string; }

export const Route = createFileRoute("/_authed/settings/users")({
  component: UsersPage,
});

// ── styles ─────────────────────────────────────────────────────────────────
const btnPrimary: React.CSSProperties = {
  background: "var(--brand)", color: "#fff", padding: "7px 14px",
  borderRadius: 8, fontSize: 13, fontWeight: 500, border: "none", cursor: "pointer",
};
const btnGhost: React.CSSProperties = {
  background: "none", color: "var(--ink-secondary)", padding: "7px 14px",
  borderRadius: 8, fontSize: 13, border: "1px solid var(--line-strong)", cursor: "pointer",
};
const btnDanger: React.CSSProperties = {
  background: "var(--brand-line)", color: "#fff", padding: "7px 14px",
  borderRadius: 8, fontSize: 13, border: "none", cursor: "pointer",
};
const inputStyle: React.CSSProperties = {
  padding: "8px 12px", background: "var(--surface-input)", border: "1px solid var(--line)",
  borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none", width: "100%",
  boxSizing: "border-box",
};

// ── shared ─────────────────────────────────────────────────────────────────
function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div {...backdropClose(() => { onClose(); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", zIndex: "var(--z-modal)" as any, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <DialogPanel onClose={() => onClose()} label={title} style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, width: 440, padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>{title}</span>
          <button aria-label="Fechar" onClick={onClose} style={{ color: "var(--ink-muted)", background: "none", border: "none", fontSize: 16, cursor: "pointer" }}><XIcon size={14} /></button>
        </div>
        {children}
      </DialogPanel>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 12, color: "var(--ink-secondary)" }}>{label}</label>
      {children}
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      style={{ ...btnGhost, padding: "5px 12px", fontSize: 12, color: copied ? "var(--ok)" : "var(--ink-secondary)", borderColor: copied ? "var(--ok-line)" : "var(--line-strong)" }}
    >
      {copied ? "Copiado!" : "Copiar"}
    </button>
  );
}

// ── CredentialsPopup — exibe login + senha após criar usuário ──────────────
function CredentialsPopup({ login, password, onClose }: { login: string; password: string; onClose: () => void }) {
  const fullText = `Login: ${login}\nSenha: ${password}`;
  return (
    <Modal title="Usuário criado" onClose={onClose}>
      <p style={{ fontSize: 13, color: "var(--ink-secondary)", margin: 0 }}>
        Guarde as credenciais agora — a senha não será exibida novamente.
      </p>
      <div style={{ background: "var(--surface-raised)", border: "1px solid var(--line)", borderRadius: 8, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>Login</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", fontFamily: "var(--font-mono)" }}>{login}</span>
        </div>
        <div style={{ borderTop: "1px solid var(--line)" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>Senha</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--warn)", fontFamily: "var(--font-mono)" }}>{password}</span>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <CopyButton text={fullText} />
        <button style={btnPrimary} onClick={onClose}>Fechar</button>
      </div>
    </Modal>
  );
}

// ── ResetPasswordPopup — exibe nova senha após reset ──────────────────────
function ResetPasswordPopup({ password, onClose }: { password: string; onClose: () => void }) {
  return (
    <Modal title="Senha redefinida" onClose={onClose}>
      <p style={{ fontSize: 13, color: "var(--ink-secondary)", margin: 0 }}>
        Nova senha gerada automaticamente. Guarde agora.
      </p>
      <div style={{ background: "var(--surface-raised)", border: "1px solid var(--line)", borderRadius: 8, padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: "var(--warn)", fontFamily: "var(--font-mono)", letterSpacing: "0.04em" }}>{password}</span>
        <CopyButton text={password} />
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button style={btnPrimary} onClick={onClose}>Fechar</button>
      </div>
    </Modal>
  );
}

// ── CreateUserModal ────────────────────────────────────────────────────────
function CreateUserModal({ roles, onClose, onCreated }: {
  roles: Role[];
  onClose: () => void;
  onCreated: (login: string, password: string) => void;
}) {
  const [form, setForm] = useState({ login: "", name: "", role_id: roles[0]?.id ?? "" });
  const mut = useMutation({
    mutationFn: () => api.post<{ login: string; generated_password: string }>("/users", form),
    onSuccess: (data) => onCreated(data.login, data.generated_password),
  });
  return (
    <Modal title="Novo usuário" onClose={onClose}>
      <Field label="Nome">
        <input style={inputStyle} placeholder="Nome completo" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </Field>
      <Field label="Login">
        <input style={inputStyle} placeholder="email@empresa.com" value={form.login} onChange={(e) => setForm({ ...form, login: e.target.value })} />
      </Field>
      <Field label="Função">
        <select style={inputStyle} value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value })}>
          {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </Field>
      <p style={{ fontSize: 12, color: "var(--ink-muted)", margin: 0 }}>
        Uma senha será gerada automaticamente após a criação.
      </p>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button style={btnGhost} onClick={onClose}>Cancelar</button>
        <button style={btnPrimary} onClick={() => mut.mutate()} disabled={mut.isPending || !form.login || !form.name}>
          {mut.isPending ? "..." : "Criar"}
        </button>
      </div>
      {mut.isError && <span style={{ fontSize: 12, color: "var(--brand)" }}>{(mut.error as any)?.payload?.detail ?? "Erro ao criar usuário."}</span>}
    </Modal>
  );
}

// ── EditUserModal — editar função + resetar senha ─────────────────────────
function EditUserModal({ user, roles, onClose, onDone }: {
  user: User;
  roles: Role[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [roleId, setRoleId] = useState(user.role_id);
  const [confirmReset, setConfirmReset] = useState(false);
  const [newPassword, setNewPassword] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: () => api.patch(`/users/${user.id}`, { role_id: roleId }),
    onSuccess: onDone,
  });

  const resetMut = useMutation({
    mutationFn: () => api.post<{ generated_password: string }>(`/users/${user.id}/reset-password`, {}),
    onSuccess: (data) => {
      setConfirmReset(false);
      setNewPassword(data.generated_password);
    },
  });

  if (newPassword) {
    return (
      <ResetPasswordPopup
        password={newPassword}
        onClose={() => { setNewPassword(null); onClose(); }}
      />
    );
  }

  if (confirmReset) {
    return (
      <Modal title="Resetar senha" onClose={() => setConfirmReset(false)}>
        <p style={{ fontSize: 13, color: "var(--ink-secondary)", margin: 0 }}>
          Deseja resetar a senha de <strong style={{ color: "var(--ink)" }}>{user.name}</strong>?
          Uma nova senha será gerada automaticamente.
        </p>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button style={btnGhost} onClick={() => setConfirmReset(false)}>Cancelar</button>
          <button
            style={{ ...btnPrimary, background: "var(--warn)", color: "var(--surface-app)" }}
            onClick={() => resetMut.mutate()}
            disabled={resetMut.isPending}
          >
            {resetMut.isPending ? "Gerando..." : "Resetar senha"}
          </button>
        </div>
        {resetMut.isError && <span style={{ fontSize: 12, color: "var(--brand)" }}>Erro ao resetar.</span>}
      </Modal>
    );
  }

  return (
    <Modal title={`Editar — ${user.name}`} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <label style={{ fontSize: 12, color: "var(--ink-muted)" }}>Login</label>
        <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>{user.login}</span>
      </div>
      <Field label="Função">
        <select style={inputStyle} value={roleId} onChange={(e) => setRoleId(e.target.value)}>
          {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </Field>
      <div style={{ borderTop: "1px solid var(--line)", paddingTop: 12 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 13, color: "var(--ink-secondary)" }}>Senha</div>
            <div style={{ fontSize: 12, color: "var(--ink-muted)", marginTop: 2 }}>Gera uma nova senha automática para o usuário</div>
          </div>
          <button
            onClick={() => setConfirmReset(true)}
            style={{ ...btnGhost, fontSize: 12, padding: "5px 12px", color: "var(--warn)", borderColor: "var(--warn-line)" }}
          >
            Resetar senha
          </button>
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button style={btnGhost} onClick={onClose}>Cancelar</button>
        <button
          style={btnPrimary}
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending || roleId === user.role_id}
        >
          {saveMut.isPending ? "..." : "Salvar"}
        </button>
      </div>
      {saveMut.isError && <span style={{ fontSize: 12, color: "var(--brand)" }}>Erro ao salvar.</span>}
    </Modal>
  );
}

// ── PauseModal ─────────────────────────────────────────────────────────────
function PauseModal({ user, users, onClose, onDone }: { user: User; users: User[]; onClose: () => void; onDone: () => void }) {
  const candidates = users.filter((u) => u.id !== user.id && !u.paused);
  const [substituteId, setSubstituteId] = useState(candidates[0]?.id ?? "");
  const mut = useMutation({
    mutationFn: () => api.post(`/users/${user.id}/pause`, { substitute_id: substituteId }),
    onSuccess: onDone,
  });
  return (
    <Modal title={`Pausar — ${user.name}`} onClose={onClose}>
      <p style={{ fontSize: 13, color: "var(--ink-secondary)", margin: 0 }}>
        O substituto será adicionado como participante nos{" "}
        <strong style={{ color: "var(--ink)" }}>{user.agents_count}</strong> agente(s) deste gestor.
        Enquanto pausado, o gestor não terá acesso ao sistema.
      </p>
      <Field label="Selecione o substituto">
        <select style={inputStyle} value={substituteId} onChange={(e) => setSubstituteId(e.target.value)}>
          {candidates.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.login})</option>)}
        </select>
      </Field>
      {candidates.length === 0 && (
        <span style={{ fontSize: 12, color: "var(--warn)" }}>Não há outros usuários ativos para substituir.</span>
      )}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button style={btnGhost} onClick={onClose}>Cancelar</button>
        <button style={{ ...btnPrimary, background: "var(--warn)", color: "var(--surface-app)" }} onClick={() => mut.mutate()} disabled={mut.isPending || !substituteId}>
          {mut.isPending ? "..." : "Pausar"}
        </button>
      </div>
      {mut.isError && <span style={{ fontSize: 12, color: "var(--brand)" }}>Erro ao pausar.</span>}
    </Modal>
  );
}

// ── ReactivateModal ────────────────────────────────────────────────────────
function ReactivateModal({ user, users, onClose, onDone }: { user: User; users: User[]; onClose: () => void; onDone: () => void }) {
  const substitute = users.find((u) => u.id === user.paused_substitute_id);
  const mut = useMutation({
    mutationFn: () => api.post(`/users/${user.id}/reactivate`, {}),
    onSuccess: onDone,
  });
  return (
    <Modal title={`Reativar — ${user.name}`} onClose={onClose}>
      <p style={{ fontSize: 13, color: "var(--ink-secondary)", margin: 0 }}>
        {substitute
          ? <><strong style={{ color: "var(--ink)" }}>{substitute.name}</strong> será removido como participante dos agentes deste gestor.</>
          : "O gestor será reativado."}
      </p>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button style={btnGhost} onClick={onClose}>Cancelar</button>
        <button style={{ ...btnPrimary, background: "var(--ok)" }} onClick={() => mut.mutate()} disabled={mut.isPending}>
          {mut.isPending ? "..." : "Reativar"}
        </button>
      </div>
      {mut.isError && <span style={{ fontSize: 12, color: "var(--brand)" }}>Erro ao reativar.</span>}
    </Modal>
  );
}

// ── DeleteModal ────────────────────────────────────────────────────────────
const CONFIRMATION_PHRASE = "EXCLUIR DEFINITIVAMENTE";

function DeleteModal({ user, users, onClose, onDone }: { user: User; users: User[]; onClose: () => void; onDone: () => void }) {
  const candidates = users.filter((u) => u.id !== user.id);
  const [transferTo, setTransferTo] = useState(candidates[0]?.id ?? "");
  const [confirmation, setConfirmation] = useState("");
  const mut = useMutation({
    mutationFn: () => api.delete(`/users/${user.id}`, { transfer_to_id: transferTo, confirmation }),
    onSuccess: onDone,
  });
  return (
    <Modal title={`Excluir — ${user.name}`} onClose={onClose}>
      <p style={{ fontSize: 13, color: "var(--ink-secondary)", margin: 0 }}>
        Os <strong style={{ color: "var(--ink)" }}>{user.agents_count}</strong> agente(s) serão transferidos para o gestor selecionado.
      </p>
      <Field label="Transferir agentes para">
        <select style={inputStyle} value={transferTo} onChange={(e) => setTransferTo(e.target.value)}>
          {candidates.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.login})</option>)}
        </select>
      </Field>
      <Field label={`Digite "${CONFIRMATION_PHRASE}" para confirmar`}>
        <input
          style={{ ...inputStyle, border: confirmation === CONFIRMATION_PHRASE ? "1px solid var(--brand)" : "1px solid var(--line)" }}
          placeholder={CONFIRMATION_PHRASE}
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
        />
      </Field>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button style={btnGhost} onClick={onClose}>Cancelar</button>
        <button
          style={btnDanger}
          onClick={() => mut.mutate()}
          disabled={mut.isPending || confirmation !== CONFIRMATION_PHRASE || !transferTo}
        >
          {mut.isPending ? "..." : "Excluir permanentemente"}
        </button>
      </div>
      {mut.isError && <span style={{ fontSize: 12, color: "var(--brand)" }}>Erro ao excluir.</span>}
    </Modal>
  );
}

// ── UsersPage ──────────────────────────────────────────────────────────────
function UsersPage() {
  const qc = useQueryClient();
  const routeCtx = _authedRoute.useRouteContext() as { user?: CurrentUser };
  const currentUser = routeCtx.user;
  const isAdmin = currentUser?.role_name === "Admin";

  const { data: users = [] } = useQuery<User[]>({ queryKey: ["users"], queryFn: () => api.get("/users") });
  const { data: roles = [] } = useQuery<Role[]>({ queryKey: ["roles"], queryFn: () => api.get("/roles") });

  // Backend já filtra Admin users e Admin role para não-admins, mas garantimos no frontend também
  const visibleRoles = isAdmin ? roles : roles.filter((r) => r.name !== "Admin");

  const [showCreate, setShowCreate] = useState(false);
  const [createdCreds, setCreatedCreds] = useState<{ login: string; password: string } | null>(null);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [pauseUser, setPauseUser] = useState<User | null>(null);
  const [reactivateUser, setReactivateUser] = useState<User | null>(null);
  const [deleteUser, setDeleteUser] = useState<User | null>(null);

  function refresh() { qc.invalidateQueries({ queryKey: ["users"] }); }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <h1 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Gerenciamento de Equipe</h1>
        <button style={btnPrimary} onClick={() => setShowCreate(true)}>+ Novo usuário</button>
      </div>

      <div style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--line)" }}>
              {["Usuário", "Função", "Agentes", "Status", ""].map((h) => (
                <th key={h} style={{ padding: "10px 16px", fontSize: 11, fontWeight: 600, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", textAlign: "left" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} style={{ borderTop: "1px solid var(--line)" }}>
                <td style={{ padding: "12px 16px" }}>
                  <div style={{ fontWeight: 500 }}>{u.name}</div>
                  <div style={{ fontSize: 12, color: "var(--ink-muted)" }}>{u.login}</div>
                </td>
                <td style={{ padding: "12px 16px", color: "var(--ink-secondary)", fontSize: 13 }}>{u.role_name}</td>
                <td style={{ padding: "12px 16px", color: "var(--ink-muted)", fontSize: 13 }}>{u.agents_count}</td>
                <td style={{ padding: "12px 16px" }}>
                  <span style={{
                    fontSize: 12, padding: "2px 8px", borderRadius: 99,
                    background: u.paused ? "var(--line)" : "var(--ok-soft)",
                    color: u.paused ? "var(--ink-muted)" : "var(--ok)",
                    border: `1px solid ${u.paused ? "var(--line-strong)" : "var(--ok-line)"}`,
                  }}>
                    {u.paused ? "Pausado" : "Ativo"}
                  </span>
                </td>
                <td style={{ padding: "12px 16px" }}>
                  <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                    <button onClick={() => setEditUser(u)} style={{ fontSize: 12, color: "var(--info)", background: "none", border: "none", cursor: "pointer" }}>
                      Editar
                    </button>
                    {u.paused ? (
                      <button onClick={() => setReactivateUser(u)} style={{ fontSize: 12, color: "var(--ok)", background: "none", border: "none", cursor: "pointer" }}>
                        Reativar
                      </button>
                    ) : (
                      <button onClick={() => setPauseUser(u)} style={{ fontSize: 12, color: "var(--warn)", background: "none", border: "none", cursor: "pointer" }}>
                        Pausar
                      </button>
                    )}
                    <button onClick={() => setDeleteUser(u)} style={{ fontSize: 12, color: "var(--brand)", background: "none", border: "none", cursor: "pointer" }}>
                      Excluir
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateUserModal
          roles={visibleRoles}
          onClose={() => setShowCreate(false)}
          onCreated={(login, password) => { refresh(); setShowCreate(false); setCreatedCreds({ login, password }); }}
        />
      )}
      {createdCreds && (
        <CredentialsPopup
          login={createdCreds.login}
          password={createdCreds.password}
          onClose={() => setCreatedCreds(null)}
        />
      )}
      {editUser && (
        <EditUserModal
          user={editUser}
          roles={visibleRoles}
          onClose={() => setEditUser(null)}
          onDone={() => { refresh(); setEditUser(null); }}
        />
      )}
      {pauseUser && (
        <PauseModal user={pauseUser} users={users} onClose={() => setPauseUser(null)} onDone={() => { refresh(); setPauseUser(null); }} />
      )}
      {reactivateUser && (
        <ReactivateModal user={reactivateUser} users={users} onClose={() => setReactivateUser(null)} onDone={() => { refresh(); setReactivateUser(null); }} />
      )}
      {deleteUser && (
        <DeleteModal user={deleteUser} users={users} onClose={() => setDeleteUser(null)} onDone={() => { refresh(); setDeleteUser(null); }} />
      )}
    </div>
  );
}
